import React, { useEffect, useState, useRef } from 'react';

// ** antd imports
import { Button, Col, DatePicker, Form, Input, Modal, Result, Row, Select, Space, Spin } from 'antd';
import { BookOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FormProviderProps } from 'antd/lib/form/context';

// ** Custom components imports
import { ClientModal } from 'components/ClientModal';

// ** Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import * as api from 'api';
import { postDocumentThunk, setPostState, updateRemaining } from 'store/docs';
import { updatePreferences } from 'store/preferences';

// ** Utils imports
import { arrayUnique, ClickableIcon, InputRight, Section, SelectCurrency } from '.';
import { departamentos } from 'constants/departamentos';
import { datepickerLocale } from 'constants/datepickerLocale';
import moment from 'moment';
import { ciRegex, rutRegex } from 'constants/regex';
import { formatImporte } from 'utils';
import { Link, useHistory } from 'react-router-dom';


const confirm = Modal.confirm;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 6 },
    },
};

const PrefixSelect = styled(Select)`
  width: 70x;
`;

const rutOCiSelector = (
    <Form.Item name="documentType" noStyle>
        <PrefixSelect>
            <Select.Option value="RUT">RUT</Select.Option>
            <Select.Option value="CI">CI</Select.Option>
            {/* <Select.Option value="NIFE">NIFE</Select.Option> */}
        </PrefixSelect>
    </Form.Item>
);


export const ReciboForm = () => {
    // ** Redux
    const {
        remainingRedux,
        preferences: { impuestos },
        isAdmin,
        loading,
        postState,
        error
    } = useSelector(({ docs, preferences, app }: RootState) => ({
        remainingRedux: docs.remaining,
        preferences,
        isAdmin: app.isAdmin,
        postState: docs.postState,
        error: docs.error,
        loading: docs.state === 'LOADING' //|| app.interbancario === null
    }));
    const dispatch = useDispatch();

    // ** Hooks 
    const [bigForm] = Form.useForm();
    const history = useHistory();

    // ** State
    const [remaining, setRemaining] = useState<api.Remaining[]>([]);
    const [showAgenda, setShowAgenda] = useState<boolean>(false);
    const isSubmittingRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (postState === 'ERROR') {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    }, [postState]);

    // ** Functions
    function getProximoNumero(tipo?: number) {
        const tipoData = remaining.find(r => r.CFE === tipo);
        if (tipoData) {
            return tipoData.desde + tipoData.utilizados;
        }

        return remaining[0].desde + remaining[0].utilizados;
    }

    const dateChecks = (date: moment.Moment): boolean => {
        const today = moment();
        if (date.isBefore(today)) {
            return date.isBefore(today.subtract(isAdmin ? 1220 : 40, 'days'));
        }
        //return date.isAfter(today.add(1, 'day').startOf('day'));
        return date.isAfter(today.endOf('month'));
    };

    // ** Handlers
    const handleFormChange: FormProviderProps['onFormChange'] = function (
        name,
        { changedFields }
    ) {
        if (name === 'bigForm') {
            // console.log({ changedFields });

            const documentTypeField = changedFields.find((f) =>
                Array.isArray(f.name) ? f.name.includes('documentType') : false
            );

            const montoField = changedFields.find((f) =>
                Array.isArray(f.name) ? f.name.includes('monto') : false
            );

            if (documentTypeField) {
                const documentType = documentTypeField.value;

                const tipoComprobante = documentType === 'RUT' ? 'e-Factura' : 'e-Ticket';
                const remainingByType = remaining.find((r) => r.tipo === tipoComprobante);

                if (!remainingByType) {
                    bigForm.setFieldsValue({ rut: '', cedula: '', numero: 0, serie: 'A' });
                    return;
                }

                const proximoNumero = getProximoNumero(remainingByType.CFE);
                bigForm.setFieldsValue({ rut: '', cedula: '', numero: proximoNumero, serie: remainingByType.serie });
            }

            if (montoField) {
                const montoValue = montoField.value;
                const parsedMonto = parseFloat(String(montoValue).replace(/,/g, '.'));
                bigForm.setFieldsValue({ total: formatImporte(isNaN(parsedMonto) ? 0 : parsedMonto) });
            }
        }
    };

    const handleFormFinish: FormProviderProps['onFormFinish'] = async (
        name: string,
        { values }
    ) => {
        if (name === 'bigForm') {
            // Prevent multiple submissions while loading
            if (isSubmittingRef.current || loading) {
                return;
            }
            isSubmittingRef.current = true;
            setIsSubmitting(true);

            try {
                // console.log('Form submitted with values:', values);
                const lineas = [{
                    cantidad: 1,
                    detalle: values.detalle,
                    precio: values.monto,
                    total: values.monto,
                    mnf: true
                }]

                let payload = {
                    serie: values.serie,
                    numero: values.numero,
                    fecha: values.fecha,
                    nombre: values.nombre,
                    direccion: values.direccion,
                    departamento: values.departamento,
                    ciudad: values.ciudad,
                    cedula: values.cedula,
                    rut: values.rut,
                    moneda: values.moneda,
                    formaPago: 'Crédito',
                    cfe: values.documentType === 'RUT' ? 111 : 101,
                    subtotal: 0,
                    iva: 0,
                    descuento: 0,
                    descuentoTotal: 0,
                    redondeo: 0,
                    monto_no_facturable: 0,
                    total: values.monto,
                    cobranza: 'Sí',
                    observaciones: values.observaciones,
                    lineas
                };

                const today = moment();
                const tipoComprobante = values.rut ? 'Factura' : 'Ticket';
                if (values.fecha.isAfter(today)) {
                    confirm({
                        title: '¿Seguro que desea emitir una factura con fecha futura?',
                        okText: 'Continuar',
                        cancelText: 'Cancelar',
                        onOk() {
                            dispatch(postDocumentThunk({ doc: payload, lineas, type: tipoComprobante }));
                        },
                        onCancel() {
                            isSubmittingRef.current = false;
                            setIsSubmitting(false);
                        },
                    });
                } else {
                    dispatch(postDocumentThunk({ doc: payload, lineas, type: tipoComprobante }));
                }
            } catch (error) {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
            }
        }
    };

    const handleKeyBlockdownEvent = (event: any) => {
        if (event.key === '&') {
            event.preventDefault();
        }
    }

    const handleKeydownEvent = (event: any) => {
        // const keyCode = event.keyCode || event.which;

        // if (keyCode === 190 || keyCode === 110 || keyCode === 188) {
        //   let dot_count = event.target.value.toString().match(/\./g);
        //   let comma_count = event.target.value.toString().match(/\,/g);
        //   dot_count = dot_count ? dot_count.length : 0;
        //   comma_count = comma_count ? comma_count.length : 0;
        //   if (dot_count + comma_count > 0) event.preventDefault();
        // } 

        const input = event.target as HTMLInputElement;
        const value = input.value;

        // Permite: números, backspace, delete, flechas, tab
        if (
            (event.key >= "0" && event.key <= "9") ||
            ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(event.key)
        ) {
            return;
        }

        // Permite un separador de decimal
        if ((event.key === "." || event.key === ",") && !/[.,]/.test(value)) {
            return;
        }

        // Bloquea el resto
        event.preventDefault();
    }

    const handleClient = (client: api.Client) => {
        console.log({ client })

        const documentType = client.rut ? 'RUT' : 'CI';

        const tipoComprobante = documentType === 'RUT' ? 'e-Factura' : 'e-Ticket';
        const remainingByType = remaining.find((r) => r.tipo === tipoComprobante);

        let numero = 0;
        let serie = 'A';

        if (remainingByType) {
            numero = getProximoNumero(remainingByType.CFE);
            serie = remainingByType.serie;
        }

        bigForm.setFieldsValue({
            ...client,
            documentType,
            numero,
            serie
        })

        setShowAgenda(false);
    };

    const resetState = () => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        bigForm.resetFields();
        dispatch(setPostState({ state: 'READY' }));
    };

    // ** Effect
    useEffect(() => {
        setRemaining(arrayUnique(remainingRedux, 'CFE'))
    }, [remainingRedux])

    // console.log({ remainingRedux, remaining })

    useEffect(() => {
        dispatch(updateRemaining());
        dispatch(updatePreferences());
    }, [dispatch]);

    if (postState === 'ERROR' || postState === 'SUCCESS') {
        const isSuccess = postState === 'SUCCESS';
        return (
            <Section>
                <Result
                    status={isSuccess ? 'success' : 'error'}
                    title={
                        isSuccess
                            ? 'Su documento se creó con éxito'
                            : 'A ocurrido un error al crear el documento'
                    }
                    subTitle={isSuccess ? undefined : error?.toString()}
                    extra={[
                        <Link to="/" onClick={resetState}>
                            <Button type="primary">Ir a mis Documentos</Button>
                        </Link>,
                        <Button key="buy" onClick={resetState}>
                            Ingresar otro Documento
                        </Button>,
                    ]}
                />
            </Section>
        );
    }

    return (
        <>
            <ClientModal
                type={'Client'}
                onCancel={() => setShowAgenda(false)}
                onOk={handleClient}
                visible={showAgenda}
            />
            <Spin
                size='large'
                spinning={loading}
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: "translate(-50%, -50%)"
                }}>
                <Form.Provider
                    onFormChange={handleFormChange}
                    onFormFinish={handleFormFinish}
                >
                    {remaining.length ? (
                        <Form
                            size="large"
                            form={bigForm}
                            name="bigForm"
                            {...formItemLayout}
                            labelAlign="left"
                            initialValues={{
                                nombre: '',
                                moneda: 'UYU',
                                impuestos: impuestos,
                                TipoRemito: 'Interno',
                                tipo: 0,
                                serie: remaining[0].serie,
                                numero: getProximoNumero(0),
                                fecha: moment(new Date()),
                                observaciones: '',
                                total: 0,
                                documentType: 'CI',
                                referenciaType: 'Referencia',
                                direccion: '',
                                departamento: '',
                                ciudad: '',
                                cedula: '',
                                rut: '',
                                nife: '',
                                pais: null,
                                infoAdicional: '',
                                detalle: ''
                            }}
                            hideRequiredMark
                        >
                            <Section>
                                <Row justify="space-between" gutter={32}>
                                    <Col xl={12} sm={24}>
                                        <Form.Item
                                            name="nombre"
                                            label="Nombre"
                                            shouldUpdate={(prev, curr) => prev.nombre !== curr.nombre}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Nombre es requerido',
                                                },
                                            ]}
                                        >
                                            <Input
                                                onKeyDown={handleKeyBlockdownEvent}
                                                suffix={
                                                    <ClickableIcon
                                                        title="Agenda"
                                                        type="primary"
                                                        onClick={() => setShowAgenda(true)}
                                                    >
                                                        <BookOutlined />
                                                    </ClickableIcon>
                                                }
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name="direccion"
                                            label="Dirección"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Dirección es requerido',
                                                },
                                            ]}
                                        >
                                            <Input maxLength={70} />
                                        </Form.Item>
                                        <Form.Item
                                            name="ciudad"
                                            label="Ciudad"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Ciudad es requerido',
                                                },
                                            ]}
                                        >
                                            <Input maxLength={30} />
                                        </Form.Item>
                                        <Form.Item
                                            name="departamento"
                                            label="Departamento"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Departamento es requerido',
                                                },
                                            ]}
                                        >
                                            {<Select placeholder="Seleccione Departamento">
                                                {departamentos.map((d) => (
                                                    <Select.Option key={d} value={d}>
                                                        {d}
                                                    </Select.Option>
                                                ))}
                                            </Select>}
                                        </Form.Item>
                                    </Col>
                                    <Col xl={12} sm={24}>
                                        <Form.Item
                                            name="fecha"
                                            label="Fecha"
                                            rules={[{ required: true, message: 'Fecha es requerido' }]}
                                        >
                                            <DatePicker
                                                locale={datepickerLocale}
                                                disabledDate={dateChecks}
                                                format="DD/MM/YYYY"
                                            />
                                        </Form.Item>
                                        <Form.Item label="Numero">
                                            <Input.Group compact>
                                                <Form.Item noStyle name="serie">
                                                    <Input disabled style={{ width: '20%' }} />
                                                </Form.Item>
                                                <Form.Item noStyle name="numero">
                                                    <InputRight disabled style={{ width: '80%' }} />
                                                </Form.Item>
                                            </Input.Group>
                                        </Form.Item>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, curr) =>
                                                prev.documentType !== curr.documentType
                                            }
                                        >
                                            {({ getFieldValue }) => {
                                                const documentType = getFieldValue('documentType');
                                                return documentType === 'RUT' ? (
                                                    <Form.Item
                                                        name="rut"
                                                        label="RUT"
                                                        rules={[
                                                            { required: true, message: 'RUT es requerido' },
                                                            {
                                                                pattern: rutRegex,
                                                                message: 'Ingrese un RUT Válido',
                                                                len: 12,
                                                            },
                                                        ]}
                                                    >
                                                        <Input addonBefore={rutOCiSelector} maxLength={12} />
                                                    </Form.Item>
                                                ) : (
                                                    <Form.Item
                                                        name={"cedula"}
                                                        label={"Cédula"}
                                                        rules={[
                                                            {
                                                                pattern: ciRegex,
                                                                message:
                                                                    'Ingrese una Cédula Válida ej: 4.283.298-4',
                                                            },
                                                        ]}
                                                    >
                                                        <Input
                                                            addonBefore={rutOCiSelector}
                                                            placeholder={documentType !== 'NIFE' ? 'ej: 4.283.298-4' : ''}
                                                            maxLength={12}
                                                        />
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Section>
                            <Section>

                                <Row justify="space-between" >
                                    <Col xl={12} sm={24}>
                                        <Form.Item
                                            name="detalle"
                                            label="Detalle"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Detalle es requerido',
                                                },
                                            ]}>
                                            <Input.TextArea
                                                autoSize={{ minRows: 4 }}
                                                onKeyDown={handleKeyBlockdownEvent}
                                                placeholder="Detalle del recibo"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xl={8} sm={24}>
                                        <Form.Item name="monto" label="Monto"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Monto es requerido',
                                                },
                                                {
                                                    validator: (_: any, value: any) => {
                                                        if (value === undefined || value === null || value === '') {
                                                            return Promise.resolve();
                                                        }
                                                        const parsed = parseFloat(String(value).replace(/,/g, '.'));
                                                        if (isNaN(parsed) || parsed <= 0) {
                                                            return Promise.reject(new Error('Monto debe ser un número mayor a 0'));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}>
                                            <InputRight
                                                placeholder="Monto"
                                                type="text"
                                                onKeyDown={handleKeydownEvent}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                            </Section>
                            <Section>
                                <Row justify="space-between">
                                    <Col xl={12} sm={24}>
                                        {/* <TextArea name="observaciones"  value = {state.ObservacionesValue} /> */}

                                        <Form.Item name="observaciones" label="Observaciones">
                                            <Input.TextArea
                                                autoSize={{ minRows: 4 }}
                                                onKeyDown={handleKeyBlockdownEvent}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xl={8} sm={24}>
                                        <Form.Item 
                                            label="Total"
                                            name="total" >
                                            <InputRight
                                                disabled
                                                addonBefore={
                                                    <SelectCurrency />
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row justify="end">
                                    <Space size="large">
                                        <Button onClick={history.goBack} disabled={isSubmitting || loading}>Cancelar</Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={isSubmitting || loading}
                                            disabled={isSubmitting || loading}
                                        >
                                            Aceptar
                                        </Button>
                                    </Space>
                                </Row>
                            </Section>
                        </Form>
                    ) : (
                        <Result
                            status="warning"
                            title="No se encontró numeración disponible para emitir nuevos documentos."
                            subTitle="Comuníquese con Infra Sistemas"
                        />
                    )}
                </Form.Provider>
            </Spin>
        </>

    )
};
